import { AiUsageHistory } from '@dadohr/shared/domainTypes/aiUsage';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import styled from '@emotion/styled';
import { useState } from 'react';
import {
  ItemSorters,
  ROW_HEIGHTS,
  RowsRenderer,
  useSortQueryParam
} from '../../../../../components/GroupableList';
import { Loader } from '../../../../../components/Loader';
import { IColumn } from '../../../../../components/Table/VirtualizedSortable';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../layout/Canvas';
import { FlexContainer, FlexContainerVertical } from '../../../../../layout/Flex';
import { Section } from '../../../../../layout/Section';
import {
  updateAiUsageLimits,
  useAiUsageCurrentByOrgId,
  useAiUsageHistoryByOrgId
} from '../../../../../services/aiUsage';
import { formatDate, toMoment } from '../../../../../services/time';
import { useOrgById } from '../../../../services/orgs';

const formatNumber = (n: number) => n.toLocaleString();

const FullWidthCard = styled(Card)`
  width: 100%;
`;

const LimitRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(p) => p.theme.spacing(1.5)}px 0;

  &:first-of-type {
    padding-top: ${(p) => p.theme.spacing(1)}px;
  }
`;

const LimitLabel = styled('label')`
  font-weight: 500;
  font-size: 14px;
`;

const formatTokens = (tokens: number) => {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(2)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
};

const EditLimitsDialog = ({
  open,
  onClose,
  orgId,
  currentLimits
}: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  currentLimits: { tokenLimit?: number; requestLimit?: number };
}) => {
  const [tokenLimit, setTokenLimit] = useState<string>(
    currentLimits.tokenLimit?.toString() || ''
  );
  const [requestLimit, setRequestLimit] = useState<string>(
    currentLimits.requestLimit?.toString() || ''
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateAiUsageLimits(orgId, {
        tokenLimit: tokenLimit ? parseInt(tokenLimit, 10) : null,
        requestLimit: requestLimit ? parseInt(requestLimit, 10) : null
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Edit AI Usage Limits</DialogTitle>
      <DialogContent>
        <LimitRow>
          <LimitLabel>Token Limit</LimitLabel>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            placeholder="Unlimited"
            value={tokenLimit}
            onChange={(e) => setTokenLimit(e.target.value)}
          />
        </LimitRow>
        <LimitRow>
          <LimitLabel>Request Limit</LimitLabel>
          <TextField
            type="number"
            size="small"
            variant="outlined"
            placeholder="Unlimited"
            value={requestLimit}
            onChange={(e) => setRequestLimit(e.target.value)}
          />
        </LimitRow>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type ColumnName =
  | 'periodStart'
  | 'periodEnd'
  | 'periodType'
  | 'totalTokens'
  | 'requestCount'
  | 'estimatedCost';

type D = Doc<AiUsageHistory>;
type Column = IColumn<D, ColumnName>;

const COLUMNS: Column[] = [
  {
    key: 'periodStart',
    head: () => 'Period Start',
    cell: (d) => formatDate(toMoment(d.data.periodStart), 'YYYY/MM/DD'),
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'periodEnd',
    head: () => 'Period End',
    cell: (d) => formatDate(toMoment(d.data.periodEnd), 'YYYY/MM/DD'),
    align: 'left',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'periodType',
    head: () => 'Type',
    cell: (d) => d.data.periodType,
    align: 'left',
    sortable: true,
    defaultDirection: 'asc',
    width: 100,
    flexGrow: 0
  },
  {
    key: 'totalTokens',
    head: () => 'Total Tokens',
    cell: (d) => formatTokens(d.data.totalTokens),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 120,
    flexGrow: 0
  },
  {
    key: 'requestCount',
    head: () => 'Requests',
    cell: (d) => formatNumber(d.data.requestCount),
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 100,
    flexGrow: 0
  },
  {
    key: 'estimatedCost',
    head: () => 'Est. Cost',
    cell: (d) =>
      d.data.estimatedCostUsd !== undefined
        ? `$${d.data.estimatedCostUsd.toFixed(2)}`
        : '-',
    align: 'right',
    sortable: true,
    defaultDirection: 'desc',
    width: 100,
    flexGrow: 0
  }
];

const SORTERS: ItemSorters<D> = {
  periodStart: {
    key: 'periodStart',
    items: { sort: (d) => d.data.periodStart.toMillis(), dir: 'desc' }
  },
  periodEnd: {
    key: 'periodEnd',
    items: { sort: (d) => d.data.periodEnd.toMillis(), dir: 'desc' }
  },
  periodType: {
    key: 'periodType',
    items: { sort: (d) => d.data.periodType, dir: 'asc' }
  },
  totalTokens: {
    key: 'totalTokens',
    items: { sort: (d) => d.data.totalTokens, dir: 'desc' }
  },
  requestCount: {
    key: 'requestCount',
    items: { sort: (d) => d.data.requestCount, dir: 'desc' }
  },
  estimatedCost: {
    key: 'estimatedCost',
    items: { sort: (d) => d.data.estimatedCostUsd || 0, dir: 'desc' }
  }
};

const DEFAULT_SORTER = SORTERS.periodStart;

const ROW_TO_KEY = (d: D) => d.id;

export const PageAiUsageOrgDetails = ({ orgId }: { orgId: string }) => {
  const [org, orgLoading, orgError] = useOrgById(orgId);
  const [currentUsage, usageLoading, usageError] = useAiUsageCurrentByOrgId(orgId);
  const [history, historyLoading, historyError] = useAiUsageHistoryByOrgId(orgId);
  const [[sorter, dir], setSort] = useSortQueryParam('sort', SORTERS);
  const {
    dialogOpen: editDialogOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog
  } = useDialogState();

  useErrorLogger('org', orgError);
  useErrorLogger('aiUsageCurrent', usageError);
  useErrorLogger('aiUsageHistory', historyError);

  const loading = orgLoading || usageLoading || historyLoading;

  if (loading) {
    return <Loader height={500} />;
  }

  if (!org) {
    return (
      <Section>
        <Typography>Organization not found</Typography>
      </Section>
    );
  }

  return (
    <Section>
      <CanvasBar>
        <FlexContainer>
          <Typography variant="h6">{org.data.name} - AI Usage</Typography>
        </FlexContainer>
      </CanvasBar>

      <FlexContainerVertical spacing={2} paddingTop={2} paddingBottom={2} paddingLeft={2} paddingRight={2}>
        <FullWidthCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Period
            </Typography>
            {currentUsage ? (
              <FlexContainer justifyContent="space-between" alignItems="flex-start">
                <div>
                  <Typography variant="caption" style={{ fontWeight: 500 }}>
                    Total Tokens
                  </Typography>
                  <Typography variant="h5">
                    {formatTokens(currentUsage.data.totalTokens)}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" style={{ fontWeight: 500 }}>
                    Requests
                  </Typography>
                  <Typography variant="h5">
                    {formatNumber(currentUsage.data.requestCount)}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" style={{ fontWeight: 500 }}>
                    Token Limit
                  </Typography>
                  <Typography variant="h5">
                    {currentUsage.data.tokenLimit
                      ? formatTokens(currentUsage.data.tokenLimit)
                      : 'Unlimited'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" style={{ fontWeight: 500 }}>
                    Request Limit
                  </Typography>
                  <Typography variant="h5">
                    {currentUsage.data.requestLimit
                      ? formatNumber(currentUsage.data.requestLimit)
                      : 'Unlimited'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="caption" style={{ fontWeight: 500 }}>
                    Period
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(toMoment(currentUsage.data.periodStart), 'YYYY/MM/DD')} -{' '}
                    {formatDate(toMoment(currentUsage.data.periodEnd), 'YYYY/MM/DD')}
                  </Typography>
                </div>
                <div>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    onClick={openEditDialog}
                  >
                    Edit Limits
                  </Button>
                </div>
              </FlexContainer>
            ) : (
              <Typography color="textSecondary">
                No usage data for this organization yet.
              </Typography>
            )}
          </CardContent>
        </FullWidthCard>

        <FullWidthCard>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Usage History
            </Typography>
            {history && history.length > 0 ? (
              <RowsRenderer
                variant="contained"
                columns={COLUMNS}
                rows={history}
                rowToKey={ROW_TO_KEY}
                sorter={sorter || DEFAULT_SORTER}
                sortDirection={dir}
                renderHead={true}
                headProps={{ sticky: false }}
                onHeadClick={(c, d) =>
                  setSort([SORTERS[c.key] || DEFAULT_SORTER, d])
                }
                chunkSize={30}
                rootMargin="300px"
                rowHeight={ROW_HEIGHTS.dense}
                otherProps={undefined}
              />
            ) : (
              <Typography color="textSecondary">
                No historical usage data available.
              </Typography>
            )}
          </CardContent>
        </FullWidthCard>
      </FlexContainerVertical>

      {editDialogOpen && currentUsage && (
        <EditLimitsDialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          orgId={orgId}
          currentLimits={{
            tokenLimit: currentUsage.data.tokenLimit,
            requestLimit: currentUsage.data.requestLimit
          }}
        />
      )}
    </Section>
  );
};
